import React from 'react';
import { Building, Users, Package, TrendingUp } from 'lucide-react';
import { Card } from '@smartphone-tycoon/shared-ui';
import type { CompanyState } from '@/types/company';
import type { Product, Employee } from '@/types/tycoon';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { PlaceholderText } from '@smartphone-tycoon/shared-ui';

interface CompanyOverviewProps {
  companyState: CompanyState;
  products: Product[];
  employees: Employee[];
}

export const CompanyOverview: React.FC<CompanyOverviewProps> = ({
  companyState,
  products,
  employees
}) => {
  // Handle loading state when data is not yet available
  if (!companyState) {
    return (
      <div className="p-6">
        <div className="text-center text-theme-text-muted py-8">
          Loading company data...
        </div>
      </div>
    );
  }

  const activeProducts = (products ?? []).filter(p => p.status === 'production').length;
  const totalEmployees = (employees ?? []).length;
  const monthlyPayroll = (employees ?? []).reduce((sum, emp) => sum + emp.salary, 0);

  return (
    <div className="p-6 space-y-6 h-full overflow-auto">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-theme-text-primary">Company Overview</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-theme-text-primary">Company Information</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-theme-text-secondary">Founded:</span>
                    <span className="ml-2 font-medium">January {companyState.year}</span>
                  </div>
                  <div>
                    <span className="text-theme-text-secondary">Industry:</span>
                    <span className="ml-2 font-medium">Consumer Electronics</span>
                  </div>
                  <div>
                    <span className="text-theme-text-secondary">Headquarters:</span>
                    <span className="ml-2 font-medium"><PlaceholderText text="Silicon Valley" /></span>
                  </div>
                </div>
              </div>
              <Building className="w-8 h-8 text-theme-text-muted" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-theme-text-primary">Financial Health</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-theme-text-secondary">Cash on Hand:</span>
                    <span className="ml-2 font-medium">{formatCurrency(companyState.cash)}</span>
                  </div>
                  <div>
                    <span className="text-theme-text-secondary">Monthly Payroll:</span>
                    <span className="ml-2 font-medium">{formatCurrency(monthlyPayroll)}</span>
                  </div>
                  <div>
                    <span className="text-theme-text-secondary">Credit Rating:</span>
                    <span className="ml-2 font-medium text-theme-success"><PlaceholderText text="AA+" /></span>
                  </div>
                </div>
              </div>
              <TrendingUp className="w-8 h-8 text-theme-text-muted" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-theme-text-primary">Operations</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-theme-text-secondary">Active Products:</span>
                    <span className="ml-2 font-medium">{activeProducts}</span>
                  </div>
                  <div>
                    <span className="text-theme-text-secondary">Total Employees:</span>
                    <span className="ml-2 font-medium">{totalEmployees}</span>
                  </div>
                  <div>
                    <span className="text-theme-text-secondary">Market Share:</span>
                    <span className="ml-2 font-medium">{(companyState.marketShare * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
              <Package className="w-8 h-8 text-theme-text-muted" />
            </div>
          </Card>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Company Milestones</h3>
        <Card className="p-4">
          <div className="text-center text-theme-text-muted py-8">
            <Users className="w-12 h-12 mx-auto mb-3 text-theme-text-muted" />
            <PlaceholderText text="Milestone tracking coming soon" />
          </div>
        </Card>
      </div>
    </div>
  );
};